import { Spinner, SpinnerSize, Stack, StackItem, Text } from '@fluentui/react'
import React, { useContext, useEffect, useState } from 'react'

import { ChatHistoryLoadingState, markAllNotificationsAsRead } from '../../api'
import { UserRole } from '../../api/models'
import { AppStateContext } from '../../state/AppProvider'

import ChatHistoryList from './ChatHistoryList'

import { Add, ArrowForwardIos, TipsAndUpdatesOutlined } from '@mui/icons-material'
import { Button, Drawer, IconButton } from '@mui/material'
import { filter } from 'lodash'
import { useLocation, useNavigate } from 'react-router-dom'
import { getUserNameToken, signOut } from '../../api/token'
import AdminPanelIcon from '../../assets/admin_panel_icon.svg'
import UnreadNotifications from '../../assets/bell_active_icon.svg'
import Notifications from '../../assets/bell_icon.svg'
import Book from '../../assets/book.svg'
import ChatHistory from '../../assets/chat_history.svg'
import CloseBar from '../../assets/close_bar.svg'
import Experiments from '../../assets/experiments.svg'
import Help from '../../assets/help.svg'
import Home from '../../assets/home.svg'
import Landscape from '../../assets/landscape.svg'
import Logo from '../../assets/logo.svg'
import OpenBar from '../../assets/open_bar.svg'
import { useMenu } from '../../hooks/useMenu'
import { UserInfoMenu } from '../UserInfoMenu'
import styles from './ChatHistoryPanel.module.css'
import { NotificationsMenu } from './NotificationsMenu'

interface ChatHistoryPanelProps {}

export enum ChatHistoryPanelTabs {
  History = 'History',
}

export function ChatHistoryPanel(_props: ChatHistoryPanelProps) {
  const appStateContext = useContext(AppStateContext)
  const navigate = useNavigate()
  const [userName, setUserName] = React.useState('')
  const [isChatHistoryOpen, setIsChatHistoryOpen] = React.useState(true)
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(true)
  const [isAdminPanelOpen, setIsAdminPanelOpen] = React.useState(false)
  const [isDashboardsOpen, setIsDashboardsOpen] = React.useState(false)
  const [isLearningDecodingOpen, setIsLearningDecodingOpen] = React.useState(false)
  const { anchorEl, open, handleClick, handleClose } = useMenu()
  const location = useLocation()
  const hasAccessRequestManagementPermission =
    appStateContext?.state.user?.permissions?.access_request_management === true
  const hasUsersRolesAndPermissionsManagementPermission =
    appStateContext?.state.user?.permissions?.user_management === true
  const isAdmin = appStateContext?.state.user?.role === UserRole.ADMIN
  const notifications = appStateContext?.state.notifications
  const hasUnreadNotifications = notifications
    ? filter(notifications, notification => !notification.is_read).length > 0
    : false
  const [anchorNotificationsEl, setAnchorNotificationsEl] = useState<null | HTMLElement>(null)
  const openNotificationsMenu = Boolean(anchorNotificationsEl)

  const handleClickNotifications = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorNotificationsEl(event.currentTarget)
  }

  const handleCloseNotifications = () => {
    setAnchorNotificationsEl(null)
    markAllNotificationsAsRead().then(() => {
      appStateContext?.dispatch({ type: 'MARK_ALL_NOTIFICATIONS_AS_READ' })
    })
  }

  useEffect(() => {}, [appStateContext?.state.chatHistory])
  useEffect(() => {
    getUserNameToken().then(userName => {
      setUserName(userName || '')
    })
  }, [])
  useEffect(() => {
    if (location.pathname === '/app/micro-learnings') {
      setIsLearningDecodingOpen(true)
    }
  }, [location.pathname])

  const getInitials = (userName: string): string => {
    if (!userName) return ''

    const names = userName.trim().split(' ')
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase()
    }

    return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase()
  }

  return (
    <>
      <Drawer
        open={isDrawerOpen}
        PaperProps={{ className: isDrawerOpen ? styles.drawer : styles.drawerClosed }}
        variant="permanent">
        <div className={styles.headerContainer}>
          {isDrawerOpen && (
            <img
              src={Logo}
              alt="logo"
            />
          )}
          {isDrawerOpen ? (
            <img
              src={CloseBar}
              alt="logo"
              className={styles.closeBar}
              onClick={() => {
                setIsDrawerOpen(false)
                setIsChatHistoryOpen(false)
              }}
            />
          ) : (
            <img
              src={OpenBar}
              alt="logo"
              className={styles.closeBar}
              onClick={() => {
                setIsDrawerOpen(true)
                setIsChatHistoryOpen(true)
              }}
            />
          )}
        </div>

        <div className={isDrawerOpen ? styles.contentContainer : styles.contentContainerClosed}>
          {isDrawerOpen ? (
            <Button
              className={styles.newChatButton}
              onClick={() => appStateContext?.dispatch({ type: 'SET_IS_NEW_CHAT_DIALOG_OPEN', payload: true })}>
              New Chat
            </Button>
          ) : (
            <IconButton
              className={styles.newChatButtonClosed}
              onClick={() => appStateContext?.dispatch({ type: 'SET_IS_NEW_CHAT_DIALOG_OPEN', payload: true })}>
              <Add />
            </IconButton>
          )}
          <div
            className={styles.menuItem}
            onClick={() => {
              navigate('/')
            }}>
            <img src={Home} /> {isDrawerOpen ? 'Home' : ''}
          </div>
          <div
            className={styles.menuItem}
            onClick={(event: React.MouseEvent<HTMLDivElement>) => handleClickNotifications(event)}>
            <img
              src={hasUnreadNotifications ? UnreadNotifications : Notifications}
              className={styles.notificationsIcon}
            />{' '}
            {isDrawerOpen ? 'Notifications' : ''}
          </div>
          <NotificationsMenu
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            anchorNotificationsEl={anchorNotificationsEl}
            openNotificationsMenu={openNotificationsMenu}
            handleCloseNotifications={handleCloseNotifications}
          />
          <div
            className={styles.menuItem}
            onClick={() => {
              appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
              navigate('/app/experiments')
            }}>
            <img src={Experiments} /> {isDrawerOpen ? 'All Experiments' : ''}
          </div>
          <div
            className={styles.menuItem}
            onClick={() => {
              setIsDrawerOpen(true)
              setIsDashboardsOpen(prev => !prev)
            }}>
            <img src={Landscape} /> {isDrawerOpen ? 'Dashboards' : ''}
          </div>
          {isDrawerOpen && isDashboardsOpen && (
            <>
              <Stack className={styles.adminPanelListContainer}>
                <div
                  className={
                    location.pathname !== '/app/adoption-dashboard'
                      ? styles.adminPanelListItem
                      : styles.adminPanelListItemSelected
                  }
                  onClick={() => {
                    navigate('/app/adoption-dashboard')
                  }}>
                  <span className={styles.adminPanelListItemText}>Adoption Dashboard</span>
                </div>
                <div
                  className={styles.adminPanelListItem}
                  onClick={() => {
                    window.open(appStateContext?.state.frontendSettings?.urls?.experiments_dashboard, '_blank')
                  }}>
                  <span className={styles.adminPanelListItemText}>Experiment Dashboard</span>
                </div>
              </Stack>
            </>
          )}
          {appStateContext?.state.frontendSettings?.micro_learning?.enabled && (
            <>
              <div
                className={styles.menuItem}
                onClick={() => {
                  setIsDrawerOpen(true)
                  setIsLearningDecodingOpen(prev => !prev)
                }}>
                <img src={Book} /> {isDrawerOpen ? 'Learning Decoding' : ''}
              </div>
              {isDrawerOpen && isLearningDecodingOpen && (
                <>
                  <Stack className={styles.adminPanelListContainer}>
                    <div
                      className={
                        location.pathname !== '/app/micro-learnings'
                          ? styles.adminPanelListItem
                          : styles.adminPanelListItemSelected
                      }
                      onClick={() => {
                        navigate('/app/micro-learnings')
                      }}>
                      <span className={styles.adminPanelListItemText}>Micro Learnings</span>
                    </div>
                  </Stack>
                </>
              )}
            </>
          )}
          {(hasAccessRequestManagementPermission || hasUsersRolesAndPermissionsManagementPermission) && (
            <>
              <div
                className={styles.menuItem}
                onClick={() => {
                  setIsDrawerOpen(true)
                  setIsAdminPanelOpen(prev => !prev)
                }}>
                <img src={AdminPanelIcon} /> {isDrawerOpen ? 'Admin Panel' : ''}
              </div>
              {isDrawerOpen && isAdminPanelOpen && (
                <>
                  <Stack className={styles.adminPanelListContainer}>
                    {hasAccessRequestManagementPermission && (
                      <div
                        className={
                          location.pathname !== '/app/access-request-management'
                            ? styles.adminPanelListItem
                            : styles.adminPanelListItemSelected
                        }
                        onClick={() => {
                          navigate('/app/access-request-management')
                        }}>
                        <span className={styles.adminPanelListItemText}>Access Request Management</span>
                      </div>
                    )}
                    {hasUsersRolesAndPermissionsManagementPermission && (
                      <div
                        className={
                          location.pathname !== '/app/users-roles-and-permissions-management'
                            ? styles.adminPanelListItem
                            : styles.adminPanelListItemSelected
                        }
                        onClick={() => {
                          navigate('/app/users-roles-and-permissions-management')
                        }}>
                        <span className={styles.adminPanelListItemText}>Users Roles and Permissions Management</span>
                      </div>
                    )}
                    {isAdmin && (
                      <div
                        className={
                          location.pathname !== '/app/application-analytics'
                            ? styles.adminPanelListItem
                            : styles.adminPanelListItemSelected
                        }
                        onClick={() => {
                          navigate('/app/application-analytics')
                        }}>
                        <span className={styles.adminPanelListItemText}>Application Analytics</span>
                      </div>
                    )}
                  </Stack>
                </>
              )}
            </>
          )}
          <div
            className={styles.menuItem}
            onClick={() => {
              setIsChatHistoryOpen(prev => !prev)
              setIsDrawerOpen(true)
            }}>
            <img src={ChatHistory} /> {isDrawerOpen ? 'Chat History' : ''}
          </div>
          <Stack className={styles.chatHistoryListContainer}>
            {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Success &&
              appStateContext?.state.isCosmosDBAvailable.cosmosDB &&
              isChatHistoryOpen && <ChatHistoryList />}
            {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
              appStateContext?.state.isCosmosDBAvailable && (
                <>
                  <Stack>
                    <StackItem>
                      <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 16 }}>
                        {appStateContext?.state.isCosmosDBAvailable?.status && (
                          <span>{appStateContext?.state.isCosmosDBAvailable?.status}</span>
                        )}
                        {!appStateContext?.state.isCosmosDBAvailable?.status && <span>Error loading chat history</span>}
                      </Text>
                    </StackItem>
                    <StackItem>
                      <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
                        <span>Chat history can't be saved at this time</span>
                      </Text>
                    </StackItem>
                  </Stack>
                </>
              )}
            {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading && (
              <>
                <Stack>
                  <Stack
                    horizontal
                    horizontalAlign="center"
                    verticalAlign="center"
                    style={{ width: '100%', marginTop: 10 }}>
                    <StackItem style={{ justifyContent: 'center', alignItems: 'center' }}>
                      <Spinner
                        style={{ alignSelf: 'flex-start', height: '100%', marginRight: '5px' }}
                        size={SpinnerSize.medium}
                      />
                    </StackItem>
                    <StackItem>
                      <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
                        <span style={{ whiteSpace: 'pre-wrap' }}>Loading chat history</span>
                      </Text>
                    </StackItem>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </div>
        <div className={styles.footerContainer}>
          <div
            className={styles.menuItem}
            onClick={() => {
              window.open(
                'https://groups.coca-cola.com/sites/ExperimentationAtScale/SitePages/ES-Learnings.aspx',
                '_blank',
              )
            }}>
            <img
              src={Help}
              className={styles.learningCenterIcon}
            />{' '}
            {isDrawerOpen ? 'Learning Center' : ''}
          </div>
          <div
            className={styles.menuItem}
            onClick={() => {
              navigate('/app/release-updates')
            }}>
            <TipsAndUpdatesOutlined /> {isDrawerOpen ? 'Release Updates' : ''}
          </div>
          <Button
            id="chat-history-panel-user-info"
            className={isDrawerOpen ? styles.userInfoContainer : styles.userInfoContainerClosed}
            aria-controls="chat-history-panel-user-info"
            onClick={handleClick}>
            {isDrawerOpen ? (
              <>
                {userName}
                <ArrowForwardIos className={styles.arrowIcon} />
              </>
            ) : (
              getInitials(userName)
            )}
          </Button>
          <UserInfoMenu
            anchorEl={anchorEl}
            open={open}
            handleClose={handleClose}
            signOut={signOut}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            className={styles.userInfoMenu}
            menuId="chat-history-panel-user-info"
            ariaLabelledBy="chat-history-panel-user-info"
          />
        </div>
      </Drawer>
    </>
  )
}
